import { Loader2, MapPin, Pencil, Route } from 'lucide-react';
import { DynamicIcon } from '../../components/ui/DynamicIcon';
import type { EnclosureCandidate, EnclosureDetectResponse, EnclosureExistingMatch } from './enclosureApi';

const labelClass = 'block text-sm font-medium text-gray-700 dark:text-neutral-300 mb-1';
const btnPrimary =
  'w-full py-3 bg-primary-500 text-white rounded-xl font-semibold hover:bg-primary-600 transition-colors disabled:bg-gray-300 dark:disabled:bg-neutral-600 disabled:cursor-not-allowed';
const btnSecondary =
  'w-full py-2.5 rounded-xl font-semibold border border-gray-300 dark:border-neutral-600 text-gray-800 dark:text-neutral-200 hover:bg-gray-50 dark:hover:bg-neutral-800';

export type EnclosureMethod = 'point' | 'boundary' | 'draw';

type MethodStepProps = {
  onSelect: (method: EnclosureMethod) => void;
};

export function EnclosureMethodStep({ onSelect }: MethodStepProps) {
  const options: {
    id: EnclosureMethod;
    title: string;
    desc: string;
    icon: React.ReactNode;
  }[] = [
    {
      id: 'point',
      title: 'Detect from a point',
      desc: 'Tap inside a school, park or road-bounded block. Existing polygons first, then derived enclosures.',
      icon: <MapPin size={22} className="text-primary-600 dark:text-primary-400" />,
    },
    {
      id: 'boundary',
      title: 'Select boundary features',
      desc: 'Tap surrounding roads or lines, then derive closed polygon candidates.',
      icon: <Route size={22} className="text-emerald-600 dark:text-emerald-400" />,
    },
    {
      id: 'draw',
      title: 'Manual capture',
      desc: 'Draw on the map or GPS-trace the perimeter, then clean vertices before preview.',
      icon: <Pencil size={22} className="text-blue-600 dark:text-blue-400" />,
    },
  ];

  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-600 dark:text-neutral-400 text-center">
        How would you like to define the closed area?
      </p>
      {options.map((opt) => (
        <button
          key={opt.id}
          type="button"
          data-testid={`enclosure-method-${opt.id}`}
          onClick={() => onSelect(opt.id)}
          className="w-full flex items-start gap-3 p-4 bg-gray-50 dark:bg-neutral-800/80 rounded-2xl hover:bg-gray-100 dark:hover:bg-neutral-700/90 border border-transparent hover:border-primary-300 dark:hover:border-primary-700 transition-all text-left shadow-sm"
        >
          <div className="mt-0.5 shrink-0">{opt.icon}</div>
          <div>
            <p className="font-semibold text-sm text-gray-900 dark:text-neutral-100">{opt.title}</p>
            <p className="text-xs text-gray-500 dark:text-neutral-400 mt-0.5">{opt.desc}</p>
          </div>
        </button>
      ))}
    </div>
  );
}

type MapStepProps = {
  method: EnclosureMethod;
  boundaryRoadCount: number;
  loading: boolean;
  feedback?: string | null;
  onFindFromBoundary?: () => void;
};

export function EnclosureMapStep({
  method,
  boundaryRoadCount,
  loading,
  feedback,
  onFindFromBoundary,
}: MapStepProps) {
  const prompt =
    method === 'point'
      ? 'Tap inside the area you want to map.'
      : method === 'boundary'
        ? 'Tap each road that forms the boundary, then press Find enclosure.'
        : 'Draw the enclosure on the map, then close the polygon.';

  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-700 dark:text-neutral-300 text-center">{prompt}</p>
      {method === 'boundary' ? (
        <>
          <p className="text-xs text-center text-gray-500 dark:text-neutral-500">
            Selected roads: <span className="font-semibold">{boundaryRoadCount}</span>
          </p>
          {onFindFromBoundary ? (
            <button
              type="button"
              data-testid="enclosure-find-boundary"
              className={btnPrimary}
              disabled={boundaryRoadCount === 0 || loading}
              onClick={onFindFromBoundary}
            >
              Find enclosure
            </button>
          ) : null}
        </>
      ) : null}
      {loading ? (
        <div className="flex items-center justify-center gap-2 text-sm text-gray-600 dark:text-neutral-400 py-4">
          <Loader2 className="animate-spin" size={18} />
          Analysing geometry…
        </div>
      ) : null}
      {feedback ? (
        <p className="text-xs text-amber-800 dark:text-amber-200 bg-amber-50 dark:bg-amber-950/30 border border-amber-100 dark:border-amber-900/50 rounded-xl px-3 py-2">
          {feedback}
        </p>
      ) : null}
    </div>
  );
}

function formatArea(sqm: number): string {
  if (sqm >= 10_000) return `${(sqm / 10_000).toFixed(2)} ha`;
  return `${Math.round(sqm).toLocaleString()} m²`;
}

type PreviewStepProps = {
  detectResult: EnclosureDetectResponse | null;
  selectedCandidateId: string | null;
  onSelectCandidate: (id: string) => void;
  onEnrichExisting: () => void;
  onPreviewSelected: () => void;
  onConfirmBoundary: () => void;
  onRetry: () => void;
  previewAreaSqm?: number | null;
};

export function EnclosurePreviewStep({
  detectResult,
  selectedCandidateId,
  onSelectCandidate,
  onEnrichExisting,
  onPreviewSelected,
  onConfirmBoundary,
  onRetry,
  previewAreaSqm,
}: PreviewStepProps) {
  if (previewAreaSqm != null) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-gray-700 dark:text-neutral-300 text-center">
          Preview the outline on the map, then confirm to choose the feature type.
        </p>
        <p className="text-center text-lg font-semibold text-gray-900 dark:text-neutral-100">
          {formatArea(previewAreaSqm)}
        </p>
        <button type="button" data-testid="enclosure-confirm-boundary" className={btnPrimary} onClick={onConfirmBoundary}>
          Confirm boundary
        </button>
        <button type="button" className={btnSecondary} onClick={onRetry}>
          Try another method
        </button>
      </div>
    );
  }

  if (!detectResult) return null;

  if (detectResult.outcome === 'existing_polygon' && detectResult.existing) {
    const ex: EnclosureExistingMatch = detectResult.existing;
    return (
      <div className="space-y-3">
        <p className="text-sm text-gray-700 dark:text-neutral-300">{detectResult.message}</p>
        <div className="rounded-xl border border-gray-200 dark:border-neutral-600 bg-gray-50/80 dark:bg-neutral-800/40 px-3 py-3">
          <p className="text-sm font-semibold text-gray-900 dark:text-neutral-100">
            {ex.name ?? 'Existing mapped area'}
          </p>
          <p className="text-xs text-gray-500 dark:text-neutral-500 mt-1">
            {ex.feature_type} · {formatArea(ex.area_sqm)}
          </p>
        </div>
        <button type="button" data-testid="enclosure-enrich-existing" className={btnPrimary} onClick={onEnrichExisting}>
          Enrich existing feature
        </button>
        <button type="button" className={btnSecondary} onClick={onRetry}>
          Detect a different area
        </button>
      </div>
    );
  }

  if (detectResult.outcome === 'derived_candidates' && detectResult.candidates.length) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-gray-600 dark:text-neutral-400 text-center">{detectResult.message}</p>
        <p className={labelClass}>Choose a candidate</p>
        <div className="space-y-2 max-h-48 overflow-y-auto">
          {detectResult.candidates.map((c: EnclosureCandidate) => (
            <button
              key={c.candidate_id}
              type="button"
              data-testid={`enclosure-candidate-${c.candidate_id}`}
              onClick={() => onSelectCandidate(c.candidate_id)}
              className={`w-full text-left px-3 py-2.5 rounded-xl border text-sm ${
                selectedCandidateId === c.candidate_id
                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-950/30'
                  : 'border-gray-200 dark:border-neutral-600'
              }`}
            >
              <span className="font-medium text-gray-900 dark:text-neutral-100">
                Enclosure {c.candidate_id.toUpperCase()}
              </span>
              <span className="block text-xs text-gray-500 dark:text-neutral-500">
                {formatArea(c.area_sqm)} · from nearby roads
              </span>
            </button>
          ))}
        </div>
        <button
          type="button"
          data-testid="enclosure-preview-selected"
          className={btnPrimary}
          disabled={!selectedCandidateId}
          onClick={onPreviewSelected}
        >
          Preview selected boundary
        </button>
        <button type="button" className={btnSecondary} onClick={onRetry}>
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-700 dark:text-neutral-300 text-center">
        {detectResult.message ?? 'No closed boundary found.'}
      </p>
      <button type="button" className={btnPrimary} onClick={onRetry}>
        Choose another method
      </button>
    </div>
  );
}

export type EnclosureFeatureTypeOption = {
  id: string;
  name: string;
  description: string;
  icon: string;
};

type FeatureTypeStepProps = {
  options: EnclosureFeatureTypeOption[];
  onSelect: (featureTypeId: string) => void;
};

export function EnclosureFeatureTypeStep({ options, onSelect }: FeatureTypeStepProps) {
  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-600 dark:text-neutral-400 text-center">
        Boundary confirmed. What does this area represent?
      </p>
      <div className="grid grid-cols-2 gap-2">
        {options.map((ft) => (
          <button
            key={ft.id}
            type="button"
            data-testid={`enclosure-feature-type-${ft.id}`}
            onClick={() => onSelect(ft.id)}
            className="flex flex-col items-center gap-1.5 p-3 bg-gray-50 dark:bg-neutral-800/80 rounded-xl hover:bg-gray-100 dark:hover:bg-neutral-700/90 border border-transparent hover:border-primary-300 dark:hover:border-primary-700 transition-all text-center shadow-sm"
          >
            <div className="w-10 h-10 bg-primary-100 dark:bg-primary-900/40 rounded-full flex items-center justify-center">
              <DynamicIcon name={ft.icon} size={20} className="text-primary-600 dark:text-primary-400" />
            </div>
            <p className="font-semibold text-xs text-gray-900 dark:text-neutral-100 leading-tight">{ft.name}</p>
            <p className="text-[10px] text-gray-500 dark:text-neutral-500 leading-tight">{ft.description}</p>
          </button>
        ))}
      </div>
    </div>
  );
}
